import React from 'react';
import { BlockDto } from '../siteApi';

const HOURS_IN_DAY = 24;
const TOTAL_BLOCKS = 96; // 24 hours * 4 (15-minute blocks)
const DAY_INDICES = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export const generateScheduleSummary = (blocks: BlockDto[]): string => {
  const blocksByDay: { [key: number]: number[] } = {};
  for (let i = 0; i < 7; i++) blocksByDay[i] = [];
  for (let day = 0; day < 7; day++) {
    for (let blockIdx = 0; blockIdx < TOTAL_BLOCKS; blockIdx++) {
      blocksByDay[day].push(blockIdx);
    }
  }
  blocks.forEach((block) => {
    if (!block.active && block.dayOfWeek !== undefined && block.blockIdx !== undefined) {
      const index = blocksByDay[block.dayOfWeek].indexOf(block.blockIdx);
      if (index > -1) blocksByDay[block.dayOfWeek].splice(index, 1);
    }
  });
  const timeRangesByDay: { [key: number]: string[] } = {};
  const formatBlockTime = (blockIdx: number): string => {
    const hour = Math.floor(blockIdx / 4);
    const minute = (blockIdx % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };
  Object.entries(blocksByDay).forEach(([day, b]) => {
    const d = parseInt(day);
    timeRangesByDay[d] = [];
    if (b.length === 0) return;
    b.sort((a, c) => a - c);
    let start = b[0];
    let end = b[0];
    for (let i = 1; i < b.length; i++) {
      if (b[i] === end + 1) {
        end = b[i];
      } else {
        timeRangesByDay[d].push(`${formatBlockTime(start)} - ${formatBlockTime(end + 1)}`);
        start = b[i];
        end = b[i];
      }
    }
    timeRangesByDay[d].push(`${formatBlockTime(start)} - ${formatBlockTime(end + 1)}`);
  });
  const scheduleGroups: { [key: string]: number[] } = {};
  Object.entries(timeRangesByDay).forEach(([day, ranges]) => {
    const d = parseInt(day);
    const key = ranges.join(', ');
    if (!scheduleGroups[key]) scheduleGroups[key] = [];
    scheduleGroups[key].push(d);
  });
  const dayIndexToName: { [key: number]: string } = {};
  Object.entries(DAY_INDICES).forEach(([name, index]) => {
    dayIndexToName[index] = name;
  });
  const summary: string[] = [];
  Object.entries(scheduleGroups).forEach(([rangeKey, days]) => {
    if (rangeKey === '') return;
    days.sort((a, b) => a - b);
    let dayText = '';
    if (days.length === 7) {
      dayText = 'Every day';
    } else if (days.length > 1) {
      let isConsecutive = true;
      for (let i = 1; i < days.length; i++) {
        if (days[i] !== days[i - 1] + 1) {
          isConsecutive = false;
          break;
        }
      }
      if (isConsecutive) {
        dayText = `${dayIndexToName[days[0]]} - ${dayIndexToName[days[days.length - 1]]}`;
      } else {
        const dayNames = days.map((day) => dayIndexToName[day]);
        const lastDay = dayNames.pop();
        dayText = `${dayNames.join(', ')} and ${lastDay}`;
      }
    } else {
      dayText = dayIndexToName[days[0]];
    }
    summary.push(`${dayText}: ${rangeKey}`);
  });
  return summary.join('\n');
};

interface ScheduleSummaryProps {
  blocks: BlockDto[];
  className?: string;
}

export const ScheduleSummary: React.FC<ScheduleSummaryProps> = ({ blocks, className }) => (
  <div className={`rounded border border-gray-700 bg-gray-800/50 p-3 text-sm text-gray-300 ${className ?? ''}`}>
    <h3 className="mb-2 text-sm font-bold">Schedule Summary</h3>
    <div className="whitespace-pre-line">
      {blocks && blocks.length > 0 ? (
        generateScheduleSummary(blocks)
      ) : (
        <span className="text-gray-400">No armed periods scheduled</span>
      )}
    </div>
  </div>
);

export default ScheduleSummary;
